import React, { useState, useContext, useEffect, createContext } from 'react';
import { authService } from '../services/api';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenChecked, setTokenChecked] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = () => {
    console.log('Initializing auth state...');

    // Check for logout request in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('logout')) {
      console.log('Logout detected - clearing all auth state');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('isAuthenticated');
      sessionStorage.clear();
      setUser(null);
      setLoading(false);
      setTokenChecked(true);
      // Clean the URL
      window.history.replaceState({}, document.title, '/');
      return;
    }

    // Check localStorage for existing JWT auth
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    console.log('Auth check:', {
      hasToken: !!token,
      hasUserData: !!userData,
      tokenPreview: token?.substring(0, 20)
    });

    if (token && userData) {
      // Validate it's a proper JWT (3 parts separated by dots)
      const isValidJWT = token.includes('.') && token.split('.').length === 3;
      
      if (isValidJWT) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          localStorage.setItem('isAuthenticated', 'true');
          console.log('User loaded from localStorage:', parsedUser.email);
          setLoading(false);
          setTokenChecked(true);
          
          // Verify token is still valid with backend and refresh subscription status
          loadUserProfile();
        } catch (err) {
          console.error('Failed to parse user data:', err);
          clearAuth();
        }
      } else {
        // Invalid token format - clear everything
        console.log('Invalid token format, clearing auth');
        clearAuth();
      }
    } else {
      // No auth data
      setLoading(false);
      setTokenChecked(true);
    }
  };

  const clearAuth = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('isAuthenticated');
    setUser(null);
    setLoading(false);
    setTokenChecked(true);
  };

  const loadUserProfile = async () => {
    try {
      console.log('Verifying token with backend...');
      const response = await authService.getProfile();

      if (response.data) {
        console.log('Profile verified successfully');
        
        // Also fetch fresh subscription status
        try {
          const subResponse = await authService.getSubscription();
          if (subResponse.data?.subscription) {
            response.data.subscription = subResponse.data.subscription;
            console.log('Subscription status refreshed:', subResponse.data.subscription);
          }
        } catch (subErr) {
          console.warn('Could not refresh subscription status:', subErr);
        }
        
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        localStorage.setItem('isAuthenticated', 'true');
        setError(null);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      if (err.response?.status === 401) {
        console.log('Token expired or invalid, clearing auth');
        clearAuth();
      } else {
        console.warn('Profile check failed, keeping session alive:', err.message);
      }
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.login(credentials);
      const { user: userData, access_token, refresh_token } = response.data;
      
      // Store tokens
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      // Store user data (includes subscription info from backend)
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      setUser(userData);

      console.log('Login successful', { 
        email: userData.email,
        subscription: userData.subscription 
      });
      toast.success(`Welcome back, ${userData.first_name || userData.username || 'User'}!`);

      // Check if password change is required
      if (userData.requires_password_change) {
        return {
          success: true,
          requiresPasswordChange: true
        };
      }

      // Check subscription status and redirect accordingly
      const hasAccess = userData.subscription?.has_access || userData.role === 'admin';
      
      if (!hasAccess) {
        // No active subscription - redirect to subscribe page
        console.log('No active subscription, redirecting to subscribe');
        window.location.href = '/subscribe';
        return { success: true, requiresSubscription: true };
      }

      // Full page reload to dashboard ensures all components pick up auth state
      window.location.href = '/dashboard';
      
      return { success: true };
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to login. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.register(userData);

      if (response.data.redirect_to_payment) {
        console.log('Payment redirect required');
        const { user: newUser, access_token, refresh_token } = response.data;
        
        // Store tokens even for payment redirect
        if (access_token) {
          localStorage.setItem('token', access_token);
        }
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }
        localStorage.setItem('user', JSON.stringify(newUser));
        
        setUser(newUser);
        return {
          success: true,
          redirectToPayment: true,
          plan: response.data.plan_id
        };
      }

      const { user: newUser, access_token, refresh_token } = response.data;
      
      if (access_token) {
        localStorage.setItem('token', access_token);
      }
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('isAuthenticated', 'true');
      setUser(newUser);

      toast.success(`Welcome ${newUser.first_name || newUser.username || 'User'}!`);
      
      // Check if they need to subscribe
      const hasAccess = newUser.subscription?.has_access;
      if (!hasAccess) {
        return { 
          success: true, 
          redirectToPayment: true,
          plan: 'annual'  // Default to annual for new users
        };
      }
      
      return { success: true };
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('Logout initiated');
    
    // Clear all storage first
    localStorage.clear();
    sessionStorage.clear();
    
    // Update React state
    setUser(null);
    setError(null);
    
    toast.success('You have been logged out.');
    
    // Full page redirect to login
    window.location.href = '/login';
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const hasToken = () => {
    const token = localStorage.getItem('token');
    return token && token.includes('.') && token.split('.').length === 3;
  };

  // Check if user has an active subscription (annual or lifetime)
  const hasActiveSubscription = () => {
    // Admins always have access
    if (user?.role === 'admin') return true;
    
    // Check the subscription object from backend
    return user?.subscription?.has_access || false;
  };

  // Check if user has premium features (same as active for our model)
  const hasPremiumAccess = () => {
    if (user?.role === 'admin') return true;
    return user?.subscription?.is_premium || false;
  };

  // Get current subscription plan
  const getSubscriptionPlan = () => {
    if (user?.role === 'admin') return 'admin';
    return user?.subscription?.plan || 'none';
  };

  // Refresh subscription status from backend
  const refreshSubscription = async () => {
    try {
      const response = await authService.getSubscription();
      if (response.data?.subscription) {
        const updatedUser = { ...user, subscription: response.data.subscription };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return response.data.subscription;
      }
    } catch (err) {
      console.error('Failed to refresh subscription:', err);
    }
    return null;
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user && hasToken(),
    hasToken: hasToken(),
    isAdmin,
    refreshUser: loadUserProfile,
    tokenChecked,
    // Subscription helpers
    hasActiveSubscription,
    hasPremiumAccess,
    getSubscriptionPlan,
    refreshSubscription,
    subscription: user?.subscription || null
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;